import { useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

type BuildWhereFromFilterValues = (any) => string;

export function usePaginatedQueryV8(
  gqlQuery: DocumentNode,
  buildWhereFromFilterValues: BuildWhereFromFilterValues,
  initOrder: string,
  appStore: any,
  execFirstTime = true,
  initFilterValues = {},
  historyReplace = true,
  stateSubName = undefined,
  persistedValues = {},
  onQueryCompleted = null,
): any {
  const location = useLocation<any>();
  const history = useHistory();
  const didMountRef = useRef(false);

  const state = stateSubName ? location.state?.[stateSubName] ?? null : location.state;

  // Τοπικό state — άμεση ενημέρωση queryState πριν το re-render από history.replace
  const [filterValues, setFilterValues] = useState(
    () => state?.filterValues ?? initFilterValues,
  );

  // Συγχρονισμός από history state (browser back κ.λπ.)
  useEffect(() => {
    if (state?.filterValues) {
      setFilterValues(state.filterValues);
    }
  }, [state?.filterValues]);

  const queryState = {
    pageIndex: state?.pageIndex ?? 0,
    filterValues,
    order: state?.order ?? initOrder,
  };

  function newStateValues(values: { filterValues?: any; pageIndex?: number }): unknown {
    if (stateSubName) {
      return {
        ...location.state,
        [stateSubName]: {
          ...location.state?.[stateSubName],
          ...values,
        },
      };
    }
    return { ...location.state, ...values };
  }

  const tablePageSize = appStore.pageSize;
  const [initPageIndex, setInitPageIndex] = useState(queryState.pageIndex);

  const combinedFilters = { ...persistedValues, ...filterValues };

  const [queryParams, setQueryParams] = useState({
    offset: tablePageSize * initPageIndex,
    limit: tablePageSize,
    order: queryState.order,
    where: buildWhereFromFilterValues(combinedFilters),
  });

  const {
    loading,
    data: rawData,
    error,
    refetch,
  } = useQuery(gqlQuery, {
    variables: queryParams,
    fetchPolicy: 'network-only',
    skip: !execFirstTime,
  });

  useEffect(() => {
    if (!onQueryCompleted || !rawData) return;
    onQueryCompleted(queryParams, rawData);
  }, [onQueryCompleted, queryParams, rawData]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (queryParams.order === initOrder) {
      return;
    }
    const newQueryParams = { ...queryParams, order: initOrder };
    setQueryParams(newQueryParams);
    refetch(newQueryParams);
  }, [initOrder, queryParams.order]);

  const filtersChanged = useCallback(
    (userValues) => {
      setFilterValues(userValues);

      const mergedValues = { ...userValues, ...persistedValues };
      const where = buildWhereFromFilterValues(mergedValues);

      const newQueryParams = { ...queryParams, offset: 0, where };
      setQueryParams(newQueryParams);
      refetch(newQueryParams);

      if (historyReplace) {
        history.replace(
          location.pathname,
          newStateValues({ filterValues: userValues, pageIndex: 0 }),
        );
      }
    },
    [queryParams, refetch, historyReplace, location.pathname],
  );

  const gridPageChanged = useCallback(
    (pageIndex, pageSize: number) => {
      setInitPageIndex(pageIndex);
      const newQueryParams = {
        ...queryParams,
        offset: pageSize * pageIndex,
        limit: pageSize,
      };
      setQueryParams(newQueryParams);
      refetch(newQueryParams);

      if (historyReplace) {
        history.replace(location.pathname, newStateValues({ pageIndex }));
      }
    },
    [historyReplace, location.pathname, queryParams, refetch],
  );

  return {
    loading,
    rawData,
    data: rawData?.[Object.keys(rawData || {})[0]]?.data,
    dataCount: rawData?.[Object.keys(rawData || {})[0]]?.recordCount,
    error,
    refetch,
    queryParams,
    setQueryParams,
    initPageIndex,
    setInitPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
  };
}
